import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Icon, ICONS } from '@/lib/icons'
import { getSportStrategy } from '@/lib/sport-strategies'
import { apiClient, handleApiError } from '@/services/api'
import type { GroupStageMatch, Player, ReportGroupStageMatchResponse, Sport } from '@/types/api'

interface ReportGroupStageMatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  seriesId: string
  groupId: string | null
  match: GroupStageMatch | null
  playersById: Record<string, Player>
  sport: Sport
  setsToPlay?: number
  onReported: (response: ReportGroupStageMatchResponse) => void
}

export function ReportGroupStageMatchDialog({
  open,
  onOpenChange,
  seriesId,
  groupId,
  match,
  playersById,
  sport,
  setsToPlay,
  onReported
}: ReportGroupStageMatchDialogProps) {
  const { t } = useTranslation()
  const [scoreA, setScoreA] = useState('')
  const [scoreB, setScoreB] = useState('')
  const [outcome, setOutcome] = useState<'A' | 'B' | 'DRAW'>('A')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isChess = sport === 'SPORT_CHESS'

  useEffect(() => {
    if (!open || !match) {
      return
    }

    setScoreA('0')
    setScoreB('0')

    const playedAt = match.matchTime ? new Date(match.matchTime) : new Date()
    setDate(playedAt.toISOString().slice(0, 10))
    setTime(playedAt.toTimeString().slice(0, 5))
  }, [open, match])

  if (!match || !groupId) {
    return null
  }

  const playerAName = match.playerAId
    ? playersById[match.playerAId]?.displayName || t('players.unknownPlayer', 'Unknown player')
    : t('groupStage.view.tbd', 'TBD')

  const playerBName = match.playerBId
    ? playersById[match.playerBId]?.displayName || t('players.unknownPlayer', 'Unknown player')
    : t('groupStage.view.tbd', 'TBD')

  // Check if current scores are valid (for enabling/disabling submit button)
  const isScoreValid = () => {
    if (isChess) {
      // Chess outcome is always valid (one of three radio options)
      return true
    }

    // For other sports, parse scores
    const parsedA = Number.parseInt(scoreA, 10)
    const parsedB = Number.parseInt(scoreB, 10)

    if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) {
      return false
    }

    // Use sport-specific strategy to validate
    const strategy = getSportStrategy(sport)
    const validation = strategy.validateScore(parsedA, parsedB, {
      isKnockout: false,
      setsToPlay: setsToPlay
    })

    return validation.valid
  }

  const validateScores = () => {
    let parsedA: number
    let parsedB: number

    if (isChess) {
      // For chess, convert outcome to internal encoding
      if (outcome === 'A') {
        parsedA = 2 // Win
        parsedB = 0 // Loss
      } else if (outcome === 'B') {
        parsedA = 0 // Loss
        parsedB = 2 // Win
      } else {
        parsedA = 1 // Draw
        parsedB = 1 // Draw
      }
    } else {
      // For other sports, parse the entered scores
      parsedA = Number.parseInt(scoreA, 10)
      parsedB = Number.parseInt(scoreB, 10)

      if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) {
        toast.error(t('groupStage.dialog.invalidScore', 'Enter a valid score for both players'))
        return null
      }
    }

    // Use sport-specific strategy for validation
    const strategy = getSportStrategy(sport)
    const validation = strategy.validateScore(parsedA, parsedB, {
      isKnockout: false, // Group stage always allows draws if sport supports it
      setsToPlay: setsToPlay
    })

    if (!validation.valid) {
      toast.error(t(validation.errorKey))
      return null
    }

    return { scoreA: parsedA, scoreB: parsedB }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const validated = validateScores()
    if (!validated) {
      return
    }

    const matchTime = date && time ? new Date(`${date}T${time}:00`).toISOString() : undefined

    try {
      setSubmitting(true)
      const response = await apiClient.reportGroupStageMatch(seriesId, groupId, match.id, {
        seriesId,
        groupId,
        matchId: match.id,
        scoreA: validated.scoreA,
        scoreB: validated.scoreB,
        matchTime
      })
      toast.success(t('groupStage.dialog.reported', 'Match result saved'))
      onReported(response)
      onOpenChange(false)
    } catch (error: unknown) {
      handleApiError(error, apiError => {
        toast.error(apiError.message || t('errors.generic'))
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Icon icon={ICONS.edit} width={20} height={20} />
            {t('groupStage.dialog.title', 'Report group match result')}
          </DialogTitle>
          <DialogDescription>
            {t('groupStage.dialog.description', 'Enter the score to update the standings.')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="font-semibold text-foreground">{playerAName}</div>
              <div className="text-muted-foreground">vs.</div>
              <div className="font-semibold text-foreground">{playerBName}</div>
            </div>

            {isChess ? (
              <div className="space-y-3">
                <Label>{t('matches.outcome.label', 'Match result')}</Label>
                <RadioGroup value={outcome} onValueChange={(value) => setOutcome(value as 'A' | 'B' | 'DRAW')}>
                  <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                    <RadioGroupItem value="A" id="outcome_a" />
                    <Label htmlFor="outcome_a" className="flex-1 cursor-pointer">
                      {playerAName} {t('common.wins', 'vinner')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                    <RadioGroupItem value="B" id="outcome_b" />
                    <Label htmlFor="outcome_b" className="flex-1 cursor-pointer">
                      {playerBName} {t('common.wins', 'vinner')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                    <RadioGroupItem value="DRAW" id="outcome_draw" />
                    <Label htmlFor="outcome_draw" className="flex-1 cursor-pointer">
                      {t('matches.outcome.draw', 'Oavgjort')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="group-score-a">{playerAName}</Label>
                  <Input
                    id="group-score-a"
                    type="number"
                    min={getSportStrategy(sport).getScoreDisplay().minScore}
                    max={getSportStrategy(sport).getScoreDisplay().maxScore}
                    step={getSportStrategy(sport).getScoreDisplay().stepScore}
                    placeholder={getSportStrategy(sport).getScoreDisplay().scorePlaceholder}
                    value={scoreA}
                    onChange={event => setScoreA(event.target.value)}
                    onInput={event => setScoreA((event.target as HTMLInputElement).value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group-score-b">{playerBName}</Label>
                  <Input
                    id="group-score-b"
                    type="number"
                    min={getSportStrategy(sport).getScoreDisplay().minScore}
                    max={getSportStrategy(sport).getScoreDisplay().maxScore}
                    step={getSportStrategy(sport).getScoreDisplay().stepScore}
                    placeholder={getSportStrategy(sport).getScoreDisplay().scorePlaceholder}
                    value={scoreB}
                    onChange={event => setScoreB(event.target.value)}
                    onInput={event => setScoreB((event.target as HTMLInputElement).value)}
                    required
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="group-date">{t('matches.played_at_date')}</Label>
                <Input
                  id="group-date"
                  type="date"
                  value={date}
                  onChange={event => setDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-time">{t('matches.played_at_time')}</Label>
                <Input
                  id="group-time"
                  type="time"
                  value={time}
                  onChange={event => setTime(event.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || !isScoreValid()}
            >
              {submitting ? t('common.saving') : t('groupStage.dialog.save', 'Save result')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
